import { apiClient, type ApiEnvelope } from './client';
import type { Paginated, TransferTarget } from './agentApi';

export interface PredictiveLiveStat {
  campaign_id: number;
  name: string;
  window_minutes: number;
  dialed: number;
  answered_human: number;
  answered_machine: number;
  connected: number;
  abandoned: number;
  no_answer: number;
  answer_rate: number;
  abandon_rate: number;
  abandon_cap: number;
  over_cap: boolean;
  agents_free: number;
  in_flight: number;
}

// ── Dialer / campaign settings ─────────────────────────────────────
export type DialStrategy =
  | 'hot_blend_newest'
  | 'hot_oldest_first'
  | 'hot_blend_attempts'
  | 'hot_new_before_followup'
  | 'cold_standard'
  | 'cold_ignore_followups'
  | 'cold_followups_first';

/** 'agent' (08/09/2026) = המספר היוצא של הנציג שמחייג. */
export type CallerIdStrategy = 'fixed' | 'rotate' | 'random' | 'agent';
export type UnansweredOutcome = 'no_answer' | 'busy' | 'failed' | 'machine';

export interface RetryStep {
  attempts: number;
  interval_min: number;
}

/**
 * סולם חיוג לפי סטטוס-שיחה: סולם נפרד לכל תוצאה + ברירת מחדל. השרת מקבל
 * או רשימה שטוחה (סולם אחיד) או את המפה הזו (סולם לפי סטטוס).
 */
export type RetryLadderOutcome = UnansweredOutcome | 'default';
export type RetryLadderMap = Partial<Record<RetryLadderOutcome, RetryStep[]>>;

// ── חלוקת לידים בקמפיין ───────────────────────────────────────────────
export type LeadOwnership = 'shared' | 'assigned';
export interface CampaignLead {
  id: number;
  phone: string;
  name?: string | null;
  status: string;
  disposition?: string | null;
  attempts: number;
  assigned_agent_id?: number | null;
  assigned_agent_name?: string | null;
  /** 'crm' כשהליד הגיע מ-CRM (Kliento) */
  external_source?: string | null;
  external_id?: string | null;
}

/** מצב אינטגרציית CRM (Kliento) — GET /admin/integrations/crm */
export interface CrmIntegrationStatus {
  connected: boolean;
  status: 'none' | 'connected' | 'disconnected' | string;
  crm_name?: string | null;
  crm_workspace_id?: string | null;
  webhook_url?: string | null;
  connected_by?: { id: number; name: string; email: string } | null;
  connected_at?: string | null;
  disconnected_at?: string | null;
  last_event_at?: string | null;
  last_error?: string | null;
  deliveries_24h?: { success: number; pending: number; failed: number; dead: number };
}
export type AssignLeadsBody =
  | { mode: 'round_robin'; agent_ids: number[]; reassign?: boolean }
  | { mode: 'to_agent'; agent_id: number; lead_ids?: number[] }
  | { mode: 'clear'; lead_ids?: number[] };

export interface QuickAction {
  label: string;
  icon?: string;
  type: 'url' | 'automation';
  url?: string;
  automation_id?: number;
}

/**
 * The full set of dialer/campaign behaviour knobs. The tenant holds one of
 * these as its default; each campaign can override any subset via
 * `settings.{key}` (an absent key = inherit the tenant default).
 */
export interface DialerSettings {
  lines_per_agent: number;
  dial_strategy: DialStrategy;
  wrapup_required: boolean;
  wrapup_blocks_next: boolean;
  auto_dial_next: boolean;
  max_unanswered_per_day: number;
  retry_ladder: RetryStep[] | RetryLadderMap;
  caller_id_strategy: CallerIdStrategy;
  rotate_threshold: number;
  quick_actions: QuickAction[];
  transfer_allowed: boolean;
  amd_enabled: boolean;
  keywords: string[];
  unanswered_outcomes: UnansweredOutcome[];
}

/** The defaults endpoint also returns the read-only strategy allow-list. */
export interface DialerDefaults extends DialerSettings {
  strategies: string[];
}

// ── Campaign overview cards ────────────────────────────────────────
export interface CampaignStatRow {
  key: string;
  label: string;
  value: number;
  pct: number;
}
export interface CampaignOverviewCard {
  id: number;
  name: string;
  status: string;
  active: boolean;
  created_at: string;
  metrics: {
    lines_per_agent: number;
    active_leads: number;
    new_today: number;
    followups_today: number;
    waiting_today: number;
    /** לידים שנחסמו על ידי תקרה (ניסיונות / אי-מענה יומי / דחיית סולם). */
    capped_leads?: number;
  };
  stats: CampaignStatRow[];
}

// ── Campaign DID bank ──────────────────────────────────────────────
export interface CampaignDid {
  id: number;
  number: string;
  sort_order?: number;
  burned?: boolean;
  burned_reason?: string | null;
}
export interface CampaignDidBank {
  bank: CampaignDid[];
  available: CampaignDid[];
  rotation: { current_did_id?: number | null; unanswered?: number | null };
}

/**
 * מוכנות קמפיין לחיוג. blockers = חסם אמיתי (dialBatch ייכשל);
 * warnings = שווה לדעת אבל לא חוסם.
 */
export interface ReadinessItem {
  code: string;
  label: string;
  detail: string;
}
export interface CampaignReadiness {
  ready: boolean;
  blockers: ReadinessItem[];
  warnings: ReadinessItem[];
  counts: { leads: number; dialable_leads: number; agents: number; dids: number };
}

/** דוח ייבוא לידים — מוחזר גם מהקמת קמפיין עם רשימה. */
export interface LeadImportReport {
  imported: number;
  skipped_duplicates: number;
  invalid_count: number;
  invalid: Array<{ phone: string; reason: string }>;
  self_dial_blocked: number;
  new_contacts: number;
  attached_to_existing_contacts: number;
}

// ── Hold sound (music-on-hold + agent confirmation) ────────────────
export interface HoldSound {
  agent_url: string | null;
  uploaded: boolean;
  filename?: string | null;
}

// ── IVR prompts (הקלטות "הקש 1 למכירות") ───────────────────────────
export interface IvrPromptRow {
  id: number;
  name: string;
  source: 'upload' | 'tts';
  tts_text?: string | null;
  original_filename?: string | null;
  created_at?: string;
}

// ── RBAC — תפקידים דינמיים והרשאות ────────────────────────────────
export interface RoleRow {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  persona: 'admin' | 'agent';
  permissions?: Record<string, string[]> | null;
  is_system?: boolean;
  users_count?: number;
}

export interface RolesCatalog {
  admin_screens: Record<string, string>;
  agent_screens: Record<string, string>;
  actions: string[];
  /**
   * הפעולות האטומיות פר-מסך (מעבר ל-view/edit), עם תווית לכל אחת.
   * חובה לרנדר אותן: השרת שומר בדיוק את מה שנשלח, כך שטופס שמכיר רק
   * view/edit ימחק בשקט כל דגל אטומי — כולל הרשאות הפיקוח.
   */
  screen_actions?: Record<string, Record<string, string>>;
  /** קיבוץ המסכים לתצוגה (פרק 4.4 באפיון). */
  groups?: Record<string, string[]>;
}

export interface AgentPermissions {
  role?: { slug: string; name: string; persona: 'admin' | 'agent' };
  is_owner?: boolean;
  base: Record<string, string[]>;
  overrides: { grant?: string[]; revoke?: string[] };
  effective: string[];
  catalog?: Record<string, string>;
  actions?: string[];
  /** דגלים אטומיים פר-מסך — חובה כדי להעניק/לשלול הרשאת פיקוח בודדת. */
  screen_actions?: Record<string, Record<string, string>>;
  /** קיבוץ המסכים לתצוגה: {'לידים ואנשי קשר': ['leads','crm'], …}. ויזואלי בלבד. */
  groups?: Record<string, string[]>;
}

// ── AI reports (תובנות מוקד) ───────────────────────────────────────
export type AiReportKind = 'business' | 'agents' | 'campaigns';
export type AiReportPeriod = 'daily' | 'monthly' | 'yearly';

export interface AiCallRef { call_id: number; phone: string }
export interface AiReportItem {
  title?: string; label?: string; name?: string; question?: string; area?: string;
  details?: string; insight?: string; pct?: number; call_refs?: AiCallRef[];
}

export interface AiAgentMetrics {
  sessions: number; answered: number; avg_score: number | null; talk_sec_total: number;
  sentiment: Record<string, number>; resolution: Record<string, number>;
  campaigns_worked: { campaign_id: number; name: string; calls: number }[];
}

export interface AiAgentProfile {
  rolling_summary?: string | null;
  traits: string[];
  reports_count: number;
}
export interface AiAgentEntry {
  agent_id: number; agent_name: string; sessions: number; error?: boolean;
  metrics?: AiAgentMetrics;
  /** פרופיל מצטבר — אופי הנציג שנבנה מדוח לדוח (מוצג בטאב נציגים). */
  profile?: AiAgentProfile | null;
  campaigns_worked?: { campaign_id: number; name: string; calls: number }[];
  report?: {
    exec_summary?: string; strengths?: AiReportItem[]; improvements?: AiReportItem[];
    action_items?: AiReportItem[]; outcome?: string;
    lead_quality?: { summary?: string; call_refs?: AiCallRef[] };
    competitors?: AiReportItem[]; objections?: AiReportItem[];
    product_questions?: AiReportItem[]; non_close_reasons?: AiReportItem[];
    recommendations?: AiReportItem[];
  } | null;
}

export interface AiBusinessReport {
  exec_summary?: string; management_recommendations?: AiReportItem[]; trends?: AiReportItem[];
  competitors?: AiReportItem[]; objections?: AiReportItem[];
  agent_comparison?: (AiReportItem & { agent_id?: number })[];
  campaign_comparison?: (AiReportItem & { campaign_id?: number })[];
}

export interface AiReportEnvelope {
  kind: AiReportKind; period: AiReportPeriod;
  period_start: string; period_end?: string | null;
  status: 'missing' | 'pending' | 'running' | 'done' | 'failed' | 'no_data';
  report?: (AiBusinessReport & { agents?: AiAgentEntry[] }) | null;
  stats?: { sessions?: number; team_avg_score?: number | null } | null;
  generated_at?: string | null;
}

export interface AiReportAccess {
  allowed_roles: string[]; assignable_roles: string[]; can_view: boolean; can_edit: boolean;
}

/** תמונת-מצב חיה לכרטיס הקמפיינים (מדדי "היום"). */
export interface CampaignLiveMetrics {
  leads_today: number;
  active_leads: number;
  waiting_today: number;
  calls_today: number;
  active_agents: number;
  calls_per_agent: number | null;
}

// ── Keyword alerts ─────────────────────────────────────────────────
// המנוע (+מפתח OpenRouter) נקבעים ע"י הסופר-אדמין פר-מרכזיה; כאן
// engine מוחזר לקריאה בלבד.
export interface KeywordAlerts {
  enabled: boolean;
  engine: string;
  keywords: string[];
  agent_ids: number[];
}

// ── Team callbacks (supervisor) ────────────────────────────────────
export interface TeamCallback {
  id: number;
  phone: string;
  name?: string | null;
  scheduled_at: string;
  reason?: string | null;
  agent_id?: number | null;
  agent_name?: string | null;
  assigned_by?: string | null;
}

// ── Agent performance (live monitor) ───────────────────────────────
export interface PerformanceFunnel {
  outbound: number;
  answered: number;
  handled: number;
  deals: number;
  talk_sec: number;
}
export interface PerformanceAgent {
  agent_id: number;
  name: string;
  role: string;
  extension?: string | null;
  status: string;
  status_since: string | null;
  outbound: number;
  outbound_pct: number;
  manual: number;
  handled: number;
  deals: number;
  dialing_sec: number;
  talk_sec: number;
  combined_sec: number;
}
export interface LivePerformance {
  range: { from: string; to: string };
  funnel: PerformanceFunnel;
  agents: PerformanceAgent[];
}

// ── פרטי שיחה מלאים ל-CDR drawer (כולל הקלטה/תמלול/AI) ────────────────
export type CdrSentiment = 'positive' | 'neutral' | 'negative';

export interface CdrTranscriptLine { t: string; speaker: string; txt: string; sentiment?: CdrSentiment }

export interface CdrRecording {
  id?: number;
  file_url?: string | null;
  /** נחתם בנפרד עם download=1 — החתימה מכסה את הפרמטרים. */
  download_url?: string | null;
  has_file?: boolean;
  duration?: number;
}

export interface CdrDetail {
  call?: { meta?: { speaker_names?: { agent?: string | null; customer?: string | null } | null } | null } & Record<string, unknown>;
  recording_id?: number | null;
  recording?: CdrRecording | null;
  transcript?: { lines: CdrTranscriptLine[] } | null;
  transcript_status?: string | null;
  ai_summary?: string | null;
  ai_sentiment?: CdrSentiment | null;
  ai_highlights?: string[] | null;
  emotions?: import('@/personas/admin/pages/recordings/emotion/types').CallEmotions | null;
  emotion_enabled?: boolean;
}

// ── מספרים חסומים ────────────────────────────────────────────────────
export interface BlockedNumber {
  id: number;
  number: string;
  display?: string | null;
  reason?: string | null;
  created_at?: string | null;
}

// ── סוכני קול (read-only, tenant-scoped) ─────────────────────────────
export type VoiceAgentDirection = 'inbound' | 'outbound' | 'both';
export type VoiceAgentStatus = 'active' | 'inactive' | 'draft';

export interface AdminVoiceAgent {
  id: number;
  name: string;
  status: VoiceAgentStatus;
  direction: VoiceAgentDirection;
  flow_id?: number | null;
  tenant_id?: number | null;
  voice_name?: string | null;
  created_at?: string | null;
  tenant_calls_count?: number;
}

export interface AdminVoiceSession {
  id: number;
  direction?: string | null;
  caller_number?: string | null;
  status?: string | null;
  duration_seconds?: number | null;
  current_stage_id?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
}

export interface VoiceStageHistoryEntry {
  stage?: string | null;
  key?: string | null;
  reason?: string | null;
  at?: string | null;
}

export interface VoiceCallDetail {
  id: number;
  voice_agent_id?: number | null;
  direction?: string | null;
  caller_number?: string | null;
  status?: string | null;
  duration_seconds?: number | null;
  transcript?: string | null;
  summary?: string | null;
  variables?: Record<string, unknown> | null;
  stage_history?: VoiceStageHistoryEntry[] | null;
  current_stage_id?: string | null;
  started_at?: string | null;
  ended_at?: string | null;
}

export interface VoiceToolLog {
  id: number;
  tool_name: string;
  status?: string | null;
  duration_ms?: number | null;
  request?: unknown;
  response?: unknown;
  created_at?: string | null;
}

export interface VoiceCallDetailEnvelope {
  call: VoiceCallDetail;
  tool_logs: VoiceToolLog[];
}

export interface AdminLiveVoiceCall {
  id: number;
  voice_agent_id?: number | null;
  direction?: string | null;
  caller_number?: string | null;
  status?: string | null;
  variables?: Record<string, unknown> | null;
  current_stage_id?: string | null;
  stage_history?: VoiceStageHistoryEntry[] | null;
  started_at?: string | null;
}

export const adminApi = {
  // dashboard / live / insights
  async dashboard() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/dashboard');
    return data.data;
  },

  async live() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/live');
    return data.data;
  },

  async snoop(channelId: string, mode: 'listen' | 'whisper' | 'barge') {
    const { data } = await apiClient.post<{ success?: boolean; message?: string; data?: { snoop_channel_id?: string | null } }>(`/admin/live/snoop/${channelId}`, { mode });
    return data;
  },

  /** "רד מהאזנה" — מנתק את ערוץ ה-snoop של המנהל (teardown הדדי מנקה את השאר). */
  async stopSnoop(snoopChannelId?: string | null) {
    const { data } = await apiClient.post<{ success?: boolean; data?: { stopped?: boolean } }>(`/admin/live/snoop/stop`, { snoop_channel_id: snoopChannelId ?? undefined });
    return data;
  },

  /** Supervisor takeover of a live call (pulls it to the supervisor). */
  async takeoverCall(callId: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { message?: string }>(`/admin/live/calls/${callId}/takeover`);
    return data;
  },

  /** Agents a supervisor can transfer a live call to (online, with extension). */
  async liveTransferTargets(excludeUserId?: number): Promise<TransferTarget[]> {
    const { data } = await apiClient.get<ApiEnvelope<TransferTarget[]>>('/admin/live/transfer-targets', { params: { exclude_user_id: excludeUserId } });
    return data.data ?? [];
  },
  async transferLiveCall(callId: number, userId: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { message?: string }>(`/admin/live/calls/${callId}/transfer`, { user_id: userId });
    return data;
  },

  async queueCalls(queueId: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>(`/admin/dashboard/queue/${queueId}/calls`);
    return data.data ?? [];
  },

  async agentLive(agentId: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/live/agent/${agentId}`);
    return data.data;
  },

  /** Agent-performance funnel + per-agent breakdown for a date range (default today). */
  async livePerformance(params?: { from?: string; to?: string; agent_id?: number }): Promise<LivePerformance> {
    const { data } = await apiClient.get<ApiEnvelope<LivePerformance>>('/admin/live/performance', { params });
    return data.data!;
  },

  async agentStats(agentId: number, from?: string, to?: string) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/agents/${agentId}/stats`, { params: { from, to } });
    return data.data;
  },

  async agentHours(agentId: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ hours: unknown[] }>>(`/admin/agents/${agentId}/hours`);
    return data.data?.hours ?? [];
  },
  async saveAgentHours(agentId: number, hours: unknown[]) {
    const { data } = await apiClient.put<ApiEnvelope<{ hours: unknown[] }>>(`/admin/agents/${agentId}/hours`, { hours });
    return data.data?.hours ?? [];
  },

  async insights(from?: string, to?: string) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/insights', { params: { from, to } });
    return data.data;
  },

  // ── דוחות AI (תובנות מוקד) ────────────────────────────────────────
  async aiReport(kind: AiReportKind, period: AiReportPeriod, date: string): Promise<AiReportEnvelope> {
    const { data } = await apiClient.get<ApiEnvelope<AiReportEnvelope>>('/admin/ai-reports', { params: { kind, period, date } });
    return data.data!;
  },
  async aiReportDates(period: AiReportPeriod, year?: number, month?: number): Promise<{ date: string; status: string }[]> {
    const { data } = await apiClient.get<ApiEnvelope<{ date: string; status: string }[]>>('/admin/ai-reports/available', { params: { period, year, month } });
    return data.data ?? [];
  },
  async aiReportAccess(): Promise<AiReportAccess> {
    const { data } = await apiClient.get<ApiEnvelope<AiReportAccess>>('/admin/ai-reports/access');
    return data.data!;
  },
  async updateAiReportAccess(allowedRoles: string[]): Promise<void> {
    await apiClient.patch('/admin/ai-reports/access', { allowed_roles: allowedRoles });
  },
  async generateAiReport(period: AiReportPeriod, date: string): Promise<void> {
    await apiClient.post('/admin/ai-reports/generate', { period, date });
  },
  /** מדדי קמפיינים חיים (תמונת-מצב של היום) לכרטיס הקמפיינים. */
  async campaignLiveMetrics(): Promise<CampaignLiveMetrics> {
    const { data } = await apiClient.get<ApiEnvelope<CampaignLiveMetrics>>('/admin/insights/campaign-metrics');
    return data.data!;
  },

  // People
  async agents(params?: Record<string, unknown>): Promise<Paginated<unknown>> {
    const { data } = await apiClient.get('/admin/agents', { params });
    return data as Paginated<unknown>;
  },
  async createAgent(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/agents', payload);
    return data.data;
  },
  async updateAgent(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/agents/${id}`, patch);
    return data.data;
  },
  async deleteAgent(id: number) {
    await apiClient.delete(`/admin/agents/${id}`);
  },
  // Per-agent outbound caller-id DIDs (only meaningful when settings.per_agent_dids is on).
  // didId = the primary/default; didIds = the full allowed set for the softphone picker.
  async setAgentOutboundDid(id: number, didId: number | null, didIds?: number[]) {
    const { data } = await apiClient.put<ApiEnvelope<{ agent_id: number; outbound_did_id: number | null; outbound_did_number: string | null; outbound_did_ids?: number[] }>>(
      `/admin/agents/${id}/outbound-did`,
      { did_id: didId, ...(didIds !== undefined ? { did_ids: didIds } : {}) },
    );
    return data.data;
  },

  /** העברת שיחות לנייד הנציג. השרת מנרמל ומאמת את המספר. */
  async setAgentMobileForward(id: number, enabled: boolean, number: string | null) {
    const { data } = await apiClient.put<ApiEnvelope<{ enabled: boolean; number: string | null }>>(
      `/admin/agents/${id}/mobile-forward`,
      { enabled, number },
    );
    return data.data;
  },

  async teams(q?: string) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/teams', { params: { q } });
    return data;
  },
  async createTeam(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/teams', payload);
    return data.data;
  },
  async updateTeam(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/teams/${id}`, patch);
    return data.data;
  },
  async deleteTeam(id: number) {
    await apiClient.delete(`/admin/teams/${id}`);
  },
  async teamMembers(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ members: unknown[]; available: unknown[] }>>(`/admin/teams/${id}/members`);
    return data.data!;
  },
  async syncTeamMembers(id: number, userIds: number[]) {
    const { data } = await apiClient.put<ApiEnvelope<unknown[]>>(`/admin/teams/${id}/members`, { user_ids: userIds });
    return data.data ?? [];
  },

  async roles() {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/roles');
    return data;
  },
  /** קטלוג המסכים/פעולות למטריצת ההרשאות (RBAC דינמי). */
  async rolesCatalog(): Promise<RolesCatalog> {
    const { data } = await apiClient.get<ApiEnvelope<RolesCatalog>>('/admin/roles/catalog');
    return data.data!;
  },
  async createRole(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/roles', payload);
    return data.data;
  },
  async updateRole(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/roles/${id}`, patch);
    return data.data;
  },
  async deleteRole(id: number) {
    await apiClient.delete(`/admin/roles/${id}`);
  },
  /** הרשאות אפקטיביות + overrides של משתמש ספציפי. */
  async agentPermissions(id: number): Promise<AgentPermissions> {
    const { data } = await apiClient.get<ApiEnvelope<AgentPermissions>>(`/admin/agents/${id}/permissions`);
    return data.data!;
  },
  async updateAgentPermissions(id: number, overrides: { grant: string[]; revoke: string[] }): Promise<AgentPermissions> {
    const { data } = await apiClient.put<ApiEnvelope<AgentPermissions>>(`/admin/agents/${id}/permissions`, overrides);
    return data.data!;
  },

  // Telephony
  async queues() {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/queues');
    return data;
  },
  async createQueue(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/queues', payload);
    return data.data;
  },
  async updateQueue(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/queues/${id}`, patch);
    return data.data;
  },
  async deleteQueue(id: number) {
    await apiClient.delete(`/admin/queues/${id}`);
  },
  async queueMembers(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ members: unknown[]; available: unknown[] }>>(`/admin/queues/${id}/members`);
    return data.data ?? { members: [], available: [] };
  },
  async syncQueueMembers(id: number, members: { id: number; penalty?: number; active?: boolean }[]) {
    const { data } = await apiClient.put<ApiEnvelope<{ members: unknown[]; available: unknown[] }>>(`/admin/queues/${id}/members`, { members });
    return data.data;
  },

  async extensions(type?: string) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/extensions', { params: { type } });
    return data;
  },
  async createExtension(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/extensions', payload);
    return data.data;
  },
  async updateExtension(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/extensions/${id}`, patch);
    return data.data;
  },
  async deleteExtension(id: number) {
    await apiClient.delete(`/admin/extensions/${id}`);
  },
  async extensionSecret(id: number): Promise<{ sip_user: string; sip_password: string }> {
    const { data } = await apiClient.get<ApiEnvelope<{ sip_user: string; sip_password: string }>>(`/admin/extensions/${id}/secret`);
    return data.data!;
  },
  async regenerateExtensionSecret(id: number): Promise<{ sip_user: string; sip_password: string }> {
    const { data } = await apiClient.post<ApiEnvelope<{ sip_user: string; sip_password: string }>>(`/admin/extensions/${id}/regenerate-secret`);
    return data.data!;
  },
  async testCall(id: number, to: string): Promise<{ success: boolean; message?: string }> {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { success: boolean; message?: string }>(`/admin/extensions/${id}/test-call`, { to });
    return { success: !!data.success, message: data.message };
  },
  async dids(q?: string) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/dids', { params: { q } });
    return data;
  },
  /** Free numbers in the platform pool the business can claim with one click. */
  async availableDids(q?: string) {
    const { data } = await apiClient.get<ApiEnvelope<{ id: number; number: string; type: string }[]>>('/admin/dids-available', { params: { q } });
    return data.data ?? [];
  },
  async claimDid(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; number: string }>>(`/admin/dids/${id}/claim`);
    return data.data;
  },
  /** Direct inbound number for an agent (routes the DID to their extension). */
  async setAgentInboundDid(id: number, didId: number | null) {
    const { data } = await apiClient.put<ApiEnvelope<{ agent_id: number; inbound_did_numbers: string[] }>>(`/admin/agents/${id}/inbound-did`, { did_id: didId });
    return data.data;
  },
  async createDid(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/dids', payload);
    return data.data;
  },
  async updateDid(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/dids/${id}`, patch);
    return data.data;
  },
  async deleteDid(id: number) {
    await apiClient.delete(`/admin/dids/${id}`);
  },
  async hours() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/hours');
    return data.data;
  },
  async upsertHours(rows: unknown[]) {
    const { data } = await apiClient.post('/admin/hours', { hours: rows });
    return data;
  },
  async addHoliday(payload: Record<string, unknown>) {
    const { data } = await apiClient.post('/admin/hours/holidays', payload);
    return data;
  },
  async deleteHoliday(id: number) {
    await apiClient.delete(`/admin/hours/holidays/${id}`);
  },
  async ivr() {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/ivr');
    return data;
  },
  async createIvr(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/ivr', payload);
    return data.data;
  },
  async saveIvr(id: number, graph: unknown, extra?: Record<string, unknown>) {
    const { data } = await apiClient.patch(`/admin/ivr/${id}`, { graph, ...extra });
    return data;
  },
  async deleteIvr(id: number) {
    await apiClient.delete(`/admin/ivr/${id}`);
  },
  async ivrPrompts(): Promise<IvrPromptRow[]> {
    const { data } = await apiClient.get<ApiEnvelope<IvrPromptRow[]>>('/admin/ivr-prompts');
    return data.data ?? [];
  },
  async uploadIvrPrompt(name: string, file: File): Promise<IvrPromptRow> {
    const form = new FormData();
    form.append('name', name);
    form.append('file', file);
    const { data } = await apiClient.post<ApiEnvelope<IvrPromptRow>>('/admin/ivr-prompts', form, { headers: { 'Content-Type': 'multipart/form-data' } });
    return data.data!;
  },
  async ttsCatalog(): Promise<{ models: { id: string; name: string; hebrew: boolean; languages?: number }[]; voices: { id: string; name: string; language?: string }[]; hebrew_model: string }> {
    const { data } = await apiClient.get<ApiEnvelope<{ models: { id: string; name: string; hebrew: boolean }[]; voices: { id: string; name: string }[]; hebrew_model: string }>>('/admin/ivr-prompts/tts-catalog');
    return data.data ?? { models: [], voices: [], hebrew_model: 'eleven_v3' };
  },
  async createIvrPromptTts(name: string, text: string, opts?: { model_id?: string; voice_id?: string }): Promise<IvrPromptRow> {
    const { data } = await apiClient.post<ApiEnvelope<IvrPromptRow>>('/admin/ivr-prompts/tts', { name, text, ...opts });
    return data.data!;
  },
  async deleteIvrPrompt(id: number): Promise<void> {
    await apiClient.delete(`/admin/ivr-prompts/${id}`);
  },

  async ivrTargets(): Promise<{ queues: { value: string; label: string }[]; extensions: { value: string; label: string }[] }> {
    const { data } = await apiClient.get<ApiEnvelope<{ queues: { value: string; label: string }[]; extensions: { value: string; label: string }[] }>>('/admin/ivr/targets');
    return data.data ?? { queues: [], extensions: [] };
  },

  // Campaigns
  async campaigns(q?: string) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/campaigns', { params: { q } });
    return data;
  },
  async campaign(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<Record<string, unknown>>>(`/admin/campaigns/${id}`);
    return data.data;
  },
  /**
   * Tenant-wide dialer defaults — the full settings object a campaign inherits
   * when it doesn't override a given key. `strategies` is the read-only list of
   * available dial strategies (never sent back on save).
   */
  async dialerDefaults() {
    const { data } = await apiClient.get<ApiEnvelope<DialerDefaults>>('/admin/campaigns/dialer-defaults');
    return data.data;
  },
  /** Persist a subset of the dialer defaults (any keys except `strategies`). */
  async saveDialerDefaults(patch: Partial<DialerSettings> | number) {
    // Back-compat: a bare number is the old lines_per_agent-only call.
    const body = typeof patch === 'number' ? { lines_per_agent: patch } : patch;
    const { data } = await apiClient.put<ApiEnvelope<DialerDefaults>>('/admin/campaigns/dialer-defaults', body);
    return data.data;
  },
  /**
   * הקמת קמפיין. אשף ההקמה שולח כאן גם agent_ids / did_ids / leads,
   * והשרת בונה הכל בבקשה אחת — אחרת נוצר קמפיין שלא יכול לחייג.
   * התשובה כוללת דוח ייבוא ובדיקת מוכנות.
   */
  async createCampaign(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; name: string; status: string }> & {
      meta?: { import?: LeadImportReport | null; readiness?: CampaignReadiness };
    }>('/admin/campaigns', payload);
    return { campaign: data.data, meta: data.meta };
  },

  /** האם הקמפיין יכול לחייג, ואם לא — מה בדיוק חוסם. */
  async campaignReadiness(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<CampaignReadiness>>(`/admin/campaigns/${id}/readiness`);
    return data.data!;
  },
  async predictiveStats() {
    const { data } = await apiClient.get<ApiEnvelope<PredictiveLiveStat[]>>('/admin/campaigns/predictive-stats');
    return data.data ?? [];
  },
  async updateCampaign(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/campaigns/${id}`, patch);
    return data.data;
  },
  async deleteCampaign(id: number) {
    await apiClient.delete(`/admin/campaigns/${id}`);
  },
  /** שכפול קמפיין: הגדרות+נציגים+מספרים, בלי לידים; נוצר במצב מושהה. */
  async duplicateCampaign(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; name: string }>>(`/admin/campaigns/${id}/duplicate`);
    return data.data;
  },
  /** כמה לידים חסומים על ידי תקרה — לתצוגה לפני שמפעילים את ההחזרה. */
  async cappedLeadsCount(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<{
      capped: number;
      breakdown?: { exhausted: number; deferred: number; daily_cap: number };
    }>>(`/admin/campaigns/${id}/capped-leads`);
    return { capped: data.data?.capped ?? 0, breakdown: data.data?.breakdown };
  },
  /**
   * מחזיר לתור לידים שנחסמו על ידי תקרה. אינו נוגע ב-DNC ובלידים שכרגע
   * בשיחה — ראה CampaignLeadReset בשרת.
   */
  async returnCappedLeads(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ returned: number }> & { message?: string }>(
      `/admin/campaigns/${id}/return-capped-leads`,
    );
    return { returned: data.data?.returned ?? 0, message: data.message };
  },
  /** מפעיל את הקמפיין מחדש — כל הלידים חוזרים לתור, למעט DNC. */
  async restartCampaign(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ restarted: number }> & { message?: string }>(
      `/admin/campaigns/${id}/restart`,
    );
    return { restarted: data.data?.restarted ?? 0, message: data.message };
  },
  async campaignAgents(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<{ agents: unknown[]; available: unknown[] }>>(`/admin/campaigns/${id}/agents`);
    return data.data!;
  },
  async syncCampaignAgents(id: number, userIds: number[]) {
    const { data } = await apiClient.put<ApiEnvelope<unknown[]>>(`/admin/campaigns/${id}/agents`, { user_ids: userIds });
    return data.data ?? [];
  },
  async campaignStats(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/campaigns/${id}/stats`);
    return data.data;
  },
  /** לידים של קמפיין עם שיוך לנציג; התשובה כוללת גם lead_ownership. */
  async campaignLeads(id: number, params?: { status?: string; assigned?: string; per_page?: number; page?: number }): Promise<Paginated<CampaignLead> & { lead_ownership?: LeadOwnership }> {
    const { data } = await apiClient.get(`/admin/campaigns/${id}/leads`, { params });
    return data as Paginated<CampaignLead> & { lead_ownership?: LeadOwnership };
  },
  /** חלוקת/שיוך לידים: round_robin | to_agent | clear. מחזיר {assigned:number}. */
  async assignCampaignLeads(id: number, body: AssignLeadsBody): Promise<{ assigned: number }> {
    const { data } = await apiClient.post<ApiEnvelope<{ assigned: number }>>(`/admin/campaigns/${id}/leads/assign`, body);
    return data.data ?? { assigned: 0 };
  },

  /**
   * הסרת לידים מקמפיין: המסומנים (lead_ids) או כל התואמים לסינון (all=true
   * + אותם status/assigned שהמסך מציג). ליד בחיוג פעיל מדולג ומדווח.
   */
  async deleteCampaignLeads(
    id: number,
    body: { lead_ids?: number[]; all?: boolean; status?: string; assigned?: string },
  ): Promise<{ deleted: number; skipped_calling: number }> {
    const { data } = await apiClient.post<ApiEnvelope<{ deleted: number; skipped_calling: number }>>(
      `/admin/campaigns/${id}/leads/delete`, body,
    );
    return data.data ?? { deleted: 0, skipped_calling: 0 };
  },

  /** Detailed per-campaign overview cards (metrics + 6 stat rows). */
  /** הצטרפות עצמית של המנהל לקמפיין (חברות+שלוחה+נוכחות) — לחיוג מהמסך. */
  async joinCampaign(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ joined: boolean; extension: string; lines_per_agent: number }>>(`/admin/campaigns/${id}/join`);
    return data.data!;
  },
  async leaveCampaign(id: number) {
    await apiClient.post(`/admin/campaigns/${id}/leave`);
  },

  async campaignsOverview(): Promise<CampaignOverviewCard[]> {
    const { data } = await apiClient.get<ApiEnvelope<CampaignOverviewCard[]>>('/admin/campaigns/overview');
    return data.data ?? [];
  },

  // Per-campaign caller-id DID bank (rotation order + burned numbers).
  async campaignDids(id: number): Promise<CampaignDidBank> {
    const { data } = await apiClient.get<ApiEnvelope<CampaignDidBank>>(`/admin/campaigns/${id}/dids`);
    return data.data ?? { bank: [], available: [], rotation: {} };
  },
  async saveCampaignDids(id: number, didIds: number[]): Promise<CampaignDidBank> {
    const { data } = await apiClient.put<ApiEnvelope<CampaignDidBank>>(`/admin/campaigns/${id}/dids`, { did_ids: didIds });
    return data.data ?? { bank: [], available: [], rotation: {} };
  },
  /** Lift a "burned" flag off a DID so it can be dialed / added again. */
  async unburnDid(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/admin/dids/${id}/unburn`);
    return data.data;
  },

  // Team callbacks (supervisor view) — pending follow-ups across the tenant.
  async teamCallbacks(): Promise<TeamCallback[]> {
    const { data } = await apiClient.get<ApiEnvelope<TeamCallback[]>>('/admin/team-callbacks');
    return data.data ?? [];
  },
  async assignTeamCallback(id: number, userId: number) {
    const { data } = await apiClient.put<ApiEnvelope<TeamCallback>>(`/admin/team-callbacks/${id}/assign`, { user_id: userId });
    return data.data;
  },

  /** campaignId=null ⇒ "המאגר": כל הלידים מכל הקמפיינים, בלי כפולים. */
  async leads(campaignId: number | null, params?: string | { status?: string; agent_id?: number; q?: string; per_page?: number }) {
    // Back-compat: a bare string is treated as the status filter.
    const opts = typeof params === 'string' ? { status: params } : (params ?? {});
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/leads', { params: { ...(campaignId != null ? { campaign_id: campaignId } : {}), ...opts } });
    return data;
  },

  async importLeads(campaignId: number, leads: unknown[]) {
    const { data } = await apiClient.post('/admin/leads/import', { campaign_id: campaignId, leads });
    return data as { imported: number; skipped_duplicates: number };
  },

  async lead(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/leads/${id}`);
    return data.data;
  },
  async updateLead(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/leads/${id}`, patch);
    return data.data;
  },
  async deleteLead(id: number) {
    await apiClient.delete(`/admin/leads/${id}`);
  },
  async bulkLeads(action: 'assign' | 'status' | 'delete', leadIds: number[], extra?: { agent_id?: number | null; status?: string }) {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { affected: number }>('/admin/leads/bulk', { action, lead_ids: leadIds, ...extra });
    return data.affected ?? 0;
  },
  async leadStats(campaignId: number | null) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/leads/stats', { params: campaignId != null ? { campaign_id: campaignId } : {} });
    return data.data;
  },
  async scripts() {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/scripts');
    return data;
  },
  async createScript(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/scripts', payload);
    return data.data;
  },
  async updateScript(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/scripts/${id}`, patch);
    return data.data;
  },
  async deleteScript(id: number) {
    await apiClient.delete(`/admin/scripts/${id}`);
  },
  async scriptPlaceholders(): Promise<{ key: string; he: string; sample: string }[]> {
    const { data } = await apiClient.get<ApiEnvelope<{ key: string; he: string; sample: string }[]>>('/admin/scripts/placeholders');
    return data.data ?? [];
  },
  async duplicateScript(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/admin/scripts/${id}/duplicate`);
    return data.data;
  },

  async dispositions(campaignId?: number) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/dispositions', { params: campaignId != null ? { campaign_id: campaignId } : undefined });
    return data;
  },
  async createDisposition(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/dispositions', payload);
    return data.data;
  },
  async updateDisposition(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/dispositions/${id}`, patch);
    return data.data;
  },
  async deleteDisposition(id: number) {
    await apiClient.delete(`/admin/dispositions/${id}`);
  },
  async reorderDispositions(ids: number[]) {
    const { data } = await apiClient.post<ApiEnvelope<unknown[]>>('/admin/dispositions/reorder', { ids });
    return data.data ?? [];
  },

  // Call wrap-up tags (admin-defined)
  async callTags() {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/call-tags');
    return data;
  },
  async createCallTag(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/call-tags', payload);
    return data.data;
  },
  async updateCallTag(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/call-tags/${id}`, patch);
    return data.data;
  },
  async deleteCallTag(id: number) {
    await apiClient.delete(`/admin/call-tags/${id}`);
  },
  async dispositionStats() {
    const { data } = await apiClient.get<ApiEnvelope<{ id: number; code: string; label: string; leads: number; calls: number }[]>>('/admin/dispositions/stats');
    return data.data ?? [];
  },

  // Quality
  async recordings(params?: { q?: string; direction?: string; agent_id?: number; from?: string; to?: string; page?: number; scope?: string }) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/recordings', { params });
    return data;
  },
  async recording(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/recordings/${id}`);
    return data.data;
  },
  async deleteRecording(id: number) {
    await apiClient.delete(`/admin/recordings/${id}`);
  },
  /** Queue transcription + AI summary for a recording's call. */
  async transcribeRecording(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ transcript_status: string }>>(`/admin/recordings/${id}/transcribe`);
    return data.data;
  },

  /** מריץ ניתוח רגשי לשיחה של ההקלטה ומחזיר את התוצאה (או message). */
  async analyzeRecordingEmotions(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ emotions: unknown }> & { message?: string }>(`/admin/recordings/${id}/analyze-emotions`);
    return { emotions: data.data?.emotions as import('@/personas/admin/pages/recordings/emotion/types').CallEmotions | null, message: (data as { message?: string }).message };
  },

  async voicemails() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/admin/voicemails');
    return { items: data.data ?? [], meta: data.meta };
  },
  async markVoicemailRead(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/admin/voicemails/${id}/read`);
    return data.data;
  },
  async markVoicemailUnread(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/admin/voicemails/${id}/unread`);
    return data.data;
  },
  async deleteVoicemail(id: number) {
    await apiClient.delete(`/admin/voicemails/${id}`);
  },

  // CRM
  async crm(params?: string | { q?: string; status?: string; owner_id?: number; tag?: string; per_page?: number }) {
    const opts = typeof params === 'string' ? { q: params } : (params ?? {});
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/crm', { params: opts });
    return data;
  },

  async crmContact(id: number) {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>(`/admin/crm/${id}`);
    return data.data;
  },

  async createContact(payload: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/crm', payload);
    return data.data;
  },

  async updateContact(id: number, patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>(`/admin/crm/${id}`, patch);
    return data.data;
  },

  async deleteContact(id: number) {
    await apiClient.delete(`/admin/crm/${id}`);
  },

  async addContactNote(id: number, body: string) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>(`/admin/crm/${id}/notes`, { body });
    return data.data;
  },

  async deleteContactNote(id: number, noteId: number) {
    await apiClient.delete(`/admin/crm/${id}/notes/${noteId}`);
  },

  async bulkContacts(action: 'assign' | 'status' | 'tag' | 'delete', ids: number[], extra?: { owner_id?: number | null; status?: string; tag?: string }) {
    const { data } = await apiClient.post<ApiEnvelope<unknown> & { affected: number }>('/admin/crm/bulk', { action, ids, ...extra });
    return data.affected ?? 0;
  },

  async contactStats() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/crm/stats');
    return data.data;
  },

  async importContacts(contacts: unknown[]) {
    const { data } = await apiClient.post('/admin/crm/import', { contacts });
    return data as { imported: number; updated: number; skipped: number };
  },

  // CDR + Reports
  // ── דוחות ביצועים (/admin/reports) ─────────────────────────────
  async reports(): Promise<ReportDef[]> {
    const { data } = await apiClient.get<ApiEnvelope<ReportDef[]>>('/admin/reports');
    return data.data ?? [];
  },
  async runReport<T = unknown>(id: string, params?: { from?: string; to?: string }): Promise<T> {
    const { data } = await apiClient.get<ApiEnvelope<T>>(`/admin/reports/${id}`, { params });
    return data.data as T;
  },
  async cdr(params?: Record<string, unknown>) {
    const { data } = await apiClient.get<Paginated<unknown>>('/admin/cdr', { params });
    return data;
  },
  /** פרטי שיחה מלאים לצורך ה-drawer: הקלטה, תמלול, סיכום AI וניתוח רגשי. */
  async cdrDetail(callId: number): Promise<CdrDetail> {
    const { data } = await apiClient.get<ApiEnvelope<CdrDetail>>(`/admin/cdr/${callId}`);
    return data.data!;
  },

  // Settings
  async settings() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/settings');
    return data.data;
  },

  async updateCompany(patch: Record<string, unknown>) {
    const { data } = await apiClient.patch<ApiEnvelope<unknown>>('/admin/settings/company', patch);
    return data.data;
  },

  // DID policy: per-agent numbers vs one shared number for everyone.
  async updateTelephony(patch: { per_agent_dids: boolean; shared_did_id?: number | null }) {
    const { data } = await apiClient.patch<ApiEnvelope<{ per_agent_dids: boolean; shared_did_id: number | null }>>('/admin/settings/telephony', patch);
    return data.data;
  },

  /**
   * Upload the hold sound (music-on-hold for the customer + the agent's hold
   * confirmation blip). Multipart; the server validates type + 2MB size.
   */
  async uploadHoldSound(file: File): Promise<HoldSound> {
    const form = new FormData();
    form.append('file', file);
    const { data } = await apiClient.post<ApiEnvelope<HoldSound>>('/admin/settings/hold-sound', form);
    return data.data!;
  },
  async deleteHoldSound(): Promise<HoldSound> {
    const { data } = await apiClient.delete<ApiEnvelope<HoldSound>>('/admin/settings/hold-sound');
    return data.data ?? { agent_url: null, uploaded: false, filename: null };
  },

  /**
   * Keyword-alert settings: toggle, keywords and monitored agents. The
   * transcription engine + OpenRouter key are super-admin-only (set per
   * tenant in /super/tenants) and are ignored by this endpoint.
   */
  async updateKeywordAlerts(patch: {
    enabled?: boolean;
    keywords?: string[];
    agent_ids?: number[];
  }): Promise<KeywordAlerts> {
    const { data } = await apiClient.patch<ApiEnvelope<KeywordAlerts>>('/admin/settings/keywords', patch);
    return data.data!;
  },

  async createApiKey(name: string, scopes?: string[], expiresAt?: string | null) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; key: string; prefix: string }>>('/admin/settings/api-keys', { name, scopes, expires_at: expiresAt ?? undefined });
    return data.data!;
  },

  async createWebhook(url: string, events: string[]) {
    const { data } = await apiClient.post('/admin/settings/webhooks', { url, events });
    return data;
  },

  async integrations() {
    const { data } = await apiClient.get<ApiEnvelope<unknown[]>>('/admin/integrations');
    return data.data ?? [];
  },

  // אינטגרציית CRM (Kliento) — מצב חיבור + ניתוק (החיבור נעשה מצד ה-CRM)
  async crmIntegration(): Promise<CrmIntegrationStatus> {
    const { data } = await apiClient.get<ApiEnvelope<CrmIntegrationStatus>>('/admin/integrations/crm');
    return data.data ?? { connected: false, status: 'none' };
  },
  async disconnectCrmIntegration(): Promise<CrmIntegrationStatus> {
    const { data } = await apiClient.post<ApiEnvelope<CrmIntegrationStatus>>('/admin/integrations/crm/disconnect');
    return data.data ?? { connected: false, status: 'disconnected' };
  },

  async connectIntegration(provider: string, config?: Record<string, unknown>) {
    const { data } = await apiClient.post(`/admin/integrations/${provider}/connect`, { config });
    return data;
  },
  async disconnectIntegration(provider: string) {
    const { data } = await apiClient.post(`/admin/integrations/${provider}/disconnect`);
    return data;
  },

  async billing() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/billing');
    return data.data;
  },
  async changePlan(planId: number) {
    const { data } = await apiClient.post<ApiEnvelope<unknown>>('/admin/billing/plan', { plan_id: planId });
    return data.data;
  },

  async completeOnboarding(payload: Record<string, unknown>) {
    const { data } = await apiClient.post('/admin/onboarding/complete', payload);
    return data;
  },
  async onboardingStatus() {
    const { data } = await apiClient.get<ApiEnvelope<unknown>>('/admin/onboarding/status');
    return data.data;
  },

  // ── MOH library (per-tenant hold-music files) ────────────────────
  async mohSounds(): Promise<MohSoundItem[]> {
    const { data } = await apiClient.get<ApiEnvelope<MohSoundItem[]>>('/admin/moh-sounds');
    return data.data ?? [];
  },
  async uploadMohSound(file: File, name?: string): Promise<MohSoundItem> {
    const form = new FormData();
    form.append('file', file);
    if (name) form.append('name', name);
    const { data } = await apiClient.post<ApiEnvelope<MohSoundItem>>('/admin/moh-sounds', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data.data as MohSoundItem;
  },
  async setMohSoundDefault(id: number): Promise<MohSoundItem> {
    const { data } = await apiClient.post<ApiEnvelope<MohSoundItem>>(`/admin/moh-sounds/${id}/default`, {});
    return data.data as MohSoundItem;
  },
  async deleteMohSound(id: number): Promise<void> {
    await apiClient.delete(`/admin/moh-sounds/${id}`);
  },

  // ── SMTP (per-tenant outbound email) ─────────────────────────────
  async updateSmtp(payload: SmtpInput): Promise<SmtpSettings> {
    const { data } = await apiClient.patch<ApiEnvelope<SmtpSettings>>('/admin/settings/smtp', payload);
    return data.data as SmtpSettings;
  },
  async testSmtp(payload: SmtpInput): Promise<{ ok: boolean; message: string }> {
    const { data } = await apiClient.post<ApiEnvelope<{ ok: boolean; message: string }>>('/admin/settings/smtp/test', payload);
    return data.data as { ok: boolean; message: string };
  },
  async deleteSmtp(): Promise<void> {
    await apiClient.delete('/admin/settings/smtp');
  },

  // ── Fireberry CRM ────────────────────────────────────────────────
  async updateFireberry(payload: FireberryInput): Promise<FireberrySettings> {
    const { data } = await apiClient.patch<ApiEnvelope<FireberrySettings>>('/admin/settings/fireberry', payload);
    return data.data as FireberrySettings;
  },
  async testFireberry(payload: { token?: string; base_url?: string }): Promise<{ ok: boolean; message: string }> {
    const { data } = await apiClient.post<ApiEnvelope<{ ok: boolean; message: string }>>('/admin/settings/fireberry/test', payload);
    return data.data as { ok: boolean; message: string };
  },
  async deleteFireberry(): Promise<FireberrySettings> {
    const { data } = await apiClient.delete<ApiEnvelope<FireberrySettings>>('/admin/settings/fireberry');
    return data.data as FireberrySettings;
  },
  /** מפתח API ייעודי לפיירברי (לידים נכנסים). מוצג פעם אחת. */
  async createFireberryInboundKey(): Promise<FireberryInboundKey> {
    const { data } = await apiClient.post<ApiEnvelope<FireberryInboundKey>>('/admin/settings/fireberry/inbound-key');
    return data.data as FireberryInboundKey;
  },
  /** טבלאות (אובייקטים) בחשבון הפיירברי, כולל מותאמות אישית. */
  async fireberryObjects(): Promise<FireberryObject[]> {
    const { data } = await apiClient.get<ApiEnvelope<FireberryObject[]>>('/admin/settings/fireberry/objects');
    return data.data ?? [];
  },
  async fireberryObjectFields(objectType: number): Promise<{ fields: FireberryField[]; suggested: FireberryFieldMap }> {
    const { data } = await apiClient.get<ApiEnvelope<{ fields: FireberryField[]; suggested: FireberryFieldMap }>>(`/admin/settings/fireberry/objects/${objectType}/fields`);
    return data.data as { fields: FireberryField[]; suggested: FireberryFieldMap };
  },
  async runFireberryImport(opts?: { dry_run?: boolean; limit?: number }): Promise<FireberryImportResult> {
    const { data } = await apiClient.post<ApiEnvelope<FireberryImportResult>>('/admin/settings/fireberry/import/run', opts ?? {});
    return data.data as FireberryImportResult;
  },
  async installFireberryPresets(): Promise<{ created: { id: number; name: string }[]; total: number }> {
    const { data } = await apiClient.post<ApiEnvelope<{ created: { id: number; name: string }[]; total: number }>>('/admin/settings/fireberry/install-presets');
    return data.data as { created: { id: number; name: string }[]; total: number };
  },

  // ── Integrations Hub ─────────────────────────────────────────────
  // Webhooks 2.0
  async hubWebhooks(): Promise<{ data: HubWebhook[]; catalog: Record<string, string> }> {
    const { data } = await apiClient.get<ApiEnvelope<HubWebhook[]> & { catalog?: Record<string, string> }>('/admin/hub/webhooks');
    return { data: data.data ?? [], catalog: data.catalog ?? {} };
  },
  async createHubWebhook(payload: { name: string; description?: string; url: string; events: string[]; active?: boolean; filters?: HubWebhookFilters | null; headers?: WebhookHeader[] | null; http_method?: WebhookMethod; body_template?: string | null; content_type?: WebhookContentType; batch_window_sec?: number; batch_max?: number }) {
    const { data } = await apiClient.post<ApiEnvelope<{ id: number; signing_secret: string }>>('/admin/hub/webhooks', payload);
    return data.data!;
  },
  async updateHubWebhook(id: number, patch: Partial<{ name: string; url: string; events: string[]; active: boolean; filters: HubWebhookFilters | null; headers: WebhookHeader[] | null; http_method: WebhookMethod; body_template: string | null; content_type: WebhookContentType; batch_window_sec: number; batch_max: number }>) {
    const { data } = await apiClient.patch<ApiEnvelope<HubWebhook>>(`/admin/hub/webhooks/${id}`, patch);
    return data.data;
  },
  /** בדיקת webhook לפני שמירה (url+headers+method+body זמניים). */
  async testDraftHubWebhook(payload: { url: string; headers?: WebhookHeader[] | null; http_method?: WebhookMethod; body_template?: string | null; content_type?: WebhookContentType }) {
    const { data } = await apiClient.post<ApiEnvelope<{ ok: boolean; status?: number; latency_ms?: number; error?: string }>>('/admin/hub/webhooks/test-draft', payload);
    return data.data!;
  },
  async deleteHubWebhook(id: number) {
    await apiClient.delete(`/admin/hub/webhooks/${id}`);
  },
  async rotateHubWebhookSecret(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ signing_secret: string }>>(`/admin/hub/webhooks/${id}/rotate-secret`);
    return data.data!;
  },
  async testHubWebhook(id: number) {
    const { data } = await apiClient.post<ApiEnvelope<{ ok: boolean; status?: number; latency_ms?: number; error?: string }>>(`/admin/hub/webhooks/${id}/test`);
    return data.data!;
  },
  async hubWebhookDeliveries(id: number): Promise<HubDelivery[]> {
    const { data } = await apiClient.get<ApiEnvelope<HubDelivery[]>>(`/admin/hub/webhooks/${id}/deliveries`);
    return data.data ?? [];
  },
  async redeliverHubDelivery(deliveryId: number) {
    const { data } = await apiClient.post<ApiEnvelope<HubDelivery>>(`/admin/hub/deliveries/${deliveryId}/redeliver`);
    return data.data;
  },

  // מסך הלוגים המאוחד (/admin/logs)
  async logsTimeline(params: LogsQuery = {}): Promise<{ data: LogRow[]; next_before: string | null }> {
    const { data } = await apiClient.get<ApiEnvelope<LogRow[]> & { next_before?: string | null }>('/admin/logs', { params });
    return { data: data.data ?? [], next_before: data.next_before ?? null };
  },
  async logsSource(source: 'webhooks' | 'api' | 'events' | 'activity', params: LogsQuery = {}): Promise<{ data: LogRow[]; next_before: string | null }> {
    const { data } = await apiClient.get<ApiEnvelope<LogRow[]> & { next_before?: string | null }>(`/admin/logs/${source}`, { params });
    return { data: data.data ?? [], next_before: data.next_before ?? null };
  },
  async logsSummary(): Promise<LogsSummary> {
    const { data } = await apiClient.get<ApiEnvelope<LogsSummary>>('/admin/logs/summary');
    return data.data!;
  },

  // Catalog (events / actions / operators)
  async hubCatalog(): Promise<HubCatalog> {
    const { data } = await apiClient.get<ApiEnvelope<HubCatalog>>('/admin/hub/catalog');
    return data.data ?? { events: {}, actions: {}, operators: [] };
  },

  // Automations
  async hubAutomations(): Promise<HubAutomation[]> {
    const { data } = await apiClient.get<ApiEnvelope<HubAutomation[]>>('/admin/hub/automations');
    return data.data ?? [];
  },
  async createHubAutomation(payload: HubAutomationInput) {
    const { data } = await apiClient.post<ApiEnvelope<HubAutomation>>('/admin/hub/automations', payload);
    return data.data!;
  },
  async updateHubAutomation(id: number, patch: Partial<HubAutomationInput>) {
    const { data } = await apiClient.patch<ApiEnvelope<HubAutomation>>(`/admin/hub/automations/${id}`, patch);
    return data.data;
  },
  async deleteHubAutomation(id: number) {
    await apiClient.delete(`/admin/hub/automations/${id}`);
  },
  async testHubAutomation(id: number, payload?: Record<string, unknown>) {
    const { data } = await apiClient.post<ApiEnvelope<HubAutomationTestResult>>(`/admin/hub/automations/${id}/test`, { payload });
    return data.data!;
  },
  async hubAutomationRuns(id: number): Promise<HubAutomationRun[]> {
    const { data } = await apiClient.get<ApiEnvelope<HubAutomationRun[]>>(`/admin/hub/automations/${id}/runs`);
    return data.data ?? [];
  },
  /** Manual re-run of a failed run (spec 15.2 — "nothing is thrown away"). */
  async hubAutomationRetryRun(id: number, runId: number): Promise<void> {
    await apiClient.post(`/admin/hub/automations/${id}/runs/${runId}/retry`);
  },

  // Event log
  async hubEvents(event?: string): Promise<{ data: HubEvent[]; catalog: Record<string, string> }> {
    const { data } = await apiClient.get<ApiEnvelope<HubEvent[]> & { catalog?: Record<string, string> }>('/admin/hub/events', { params: { event: event || undefined } });
    return { data: data.data ?? [], catalog: data.catalog ?? {} };
  },

  // API keys
  async hubApiKeys(): Promise<{ data: HubApiKey[]; available_scopes: string[]; scope_catalog: ApiScopeMeta[] }> {
    const { data } = await apiClient.get<ApiEnvelope<HubApiKey[]> & { available_scopes?: string[]; scope_catalog?: ApiScopeMeta[] }>('/admin/hub/api-keys');
    return { data: data.data ?? [], available_scopes: data.available_scopes ?? [], scope_catalog: data.scope_catalog ?? [] };
  },
  async deleteHubApiKey(id: number) {
    await apiClient.delete(`/admin/hub/api-keys/${id}`);
  },

  // ── Inspector + קונסולת בדיקות ל-API הציבורי (settings?tab=api) ──
  async apiInspectorStatus(): Promise<ApiInspectorStatus> {
    const { data } = await apiClient.get<ApiEnvelope<ApiInspectorStatus>>('/admin/api-inspector');
    return data.data!;
  },
  async apiInspectorToggle(on: boolean): Promise<ApiInspectorStatus> {
    const { data } = await apiClient.post<ApiEnvelope<ApiInspectorStatus>>('/admin/api-inspector/toggle', { on });
    return data.data!;
  },
  async apiInspectorRequests(sinceId?: number): Promise<ApiInspectorRequest[]> {
    const { data } = await apiClient.get<ApiEnvelope<ApiInspectorRequest[]>>('/admin/api-inspector/requests', {
      params: { since_id: sinceId || undefined },
    });
    return data.data ?? [];
  },
  async apiInspectorClear() {
    await apiClient.delete('/admin/api-inspector/requests');
  },
  async apiTesterRun(payload: {
    method: string; path: string; api_key: string;
    query?: Record<string, string> | null; body?: unknown | null;
  }): Promise<ApiTesterResult> {
    const { data } = await apiClient.post<ApiEnvelope<ApiTesterResult>>('/admin/api-tester', payload);
    return data.data!;
  },

  // ── מספרים חסומים ────────────────────────────────────────────────
  async blockedNumbers(params?: { search?: string; per_page?: number; page?: number }): Promise<Paginated<BlockedNumber>> {
    const { data } = await apiClient.get('/admin/blocked-numbers', { params });
    return data as Paginated<BlockedNumber>;
  },
  /** `reason` is required by the server (spec 6.8, min 5 chars); `target_status` is optional. */
  async addBlockedNumber(payload: { number: string; reason: string; target_status?: string | null }): Promise<BlockedNumber> {
    const { data } = await apiClient.post<ApiEnvelope<BlockedNumber>>('/admin/blocked-numbers', payload);
    return data.data!;
  },
  async deleteBlockedNumber(id: number) {
    await apiClient.delete(`/admin/blocked-numbers/${id}`);
  },

  // ── סוכני קול (read-only) ────────────────────────────────────────
  async voiceAgents(): Promise<AdminVoiceAgent[]> {
    const { data } = await apiClient.get<ApiEnvelope<AdminVoiceAgent[]>>('/admin/voice-agents');
    return data.data ?? [];
  },
  async voiceAgentSessions(agentId: number, params?: { direction?: string; search?: string; per_page?: number; page?: number }): Promise<Paginated<AdminVoiceSession>> {
    const { data } = await apiClient.get(`/admin/voice-agents/${agentId}/sessions`, { params });
    return data as Paginated<AdminVoiceSession>;
  },
  async voiceCall(callId: number): Promise<VoiceCallDetailEnvelope> {
    const { data } = await apiClient.get<ApiEnvelope<VoiceCallDetailEnvelope>>(`/admin/voice-calls/${callId}`);
    return data.data!;
  },
  async liveVoiceCalls(): Promise<AdminLiveVoiceCall[]> {
    const { data } = await apiClient.get<ApiEnvelope<AdminLiveVoiceCall[]>>('/admin/voice-calls/live');
    return data.data ?? [];
  },
};

// ── Integrations Hub types ─────────────────────────────────────────
export interface HubWebhookFilters {
  campaign_id: number | null;
  dispositions: string[] | null;
}
export interface WebhookHeader { key: string; value: string }
export type WebhookMethod = 'POST' | 'PUT' | 'PATCH' | 'GET';
export type WebhookContentType = 'json' | 'form';
export interface HubWebhook {
  id: number;
  name: string;
  description?: string | null;
  url: string;
  events: string[];
  filters?: HubWebhookFilters | null;
  headers?: WebhookHeader[];
  http_method?: WebhookMethod;
  body_template?: string | null;
  content_type?: WebhookContentType;
  batch_window_sec?: number;
  batch_max?: number;
  active: boolean;
  deliveries_24h: number;
  success_24h: number;
  created_at: string;
}

// מסך הלוגים המאוחד
export interface LogsQuery {
  before?: string;
  limit?: number;
  q?: string;
  status?: 'ok' | 'failed' | 'stuck' | '';
  from?: string;
  to?: string;
  sources?: string;
}
export interface LogRow {
  source: 'webhook' | 'api' | 'event' | 'activity';
  id: number;
  ts: string | null;
  title: string;
  status: 'ok' | 'failed' | 'stuck' | 'pending' | null;
  raw_status?: string;
  http_status?: number | null;
  latency_ms?: number | null;
  attempts?: number;
  retryable?: boolean;
  detail?: Record<string, unknown>;
}
export interface LogsSummary {
  webhooks_24h: number;
  webhooks_failed_24h: number;
  webhooks_stuck: number;
  api_24h: number;
  api_failed_24h: number;
  events_24h: number;
  activity_24h: number;
}

export type HubDeliveryStatus = 'pending' | 'success' | 'failed' | 'dead';
export interface HubDelivery {
  id: number;
  event: string;
  payload: unknown;
  status: HubDeliveryStatus;
  attempts: number;
  http_status?: number | null;
  response_snippet?: string | null;
  latency_ms?: number | null;
  next_retry_at?: string | null;
  created_at: string;
}

export interface HubCapabilities {
  has_whatsapp: boolean;
  has_smtp: boolean;
  /** חיבור Fireberry פעיל (הגדרות → חברה → FIREBERRY). */
  has_fireberry?: boolean;
  /** Whether the dropped actions (ticket/WhatsApp/email) are offered at all. */
  legacy_actions?: boolean;
}

// ── MOH library (per-tenant hold-music files) ────────────────────
export interface MohSoundItem {
  id: number;
  name: string;
  is_default: boolean;
  filename?: string | null;
  preview_url: string;
}

// ── SMTP (per-tenant outbound email) ─────────────────────────────
export interface SmtpSettings {
  enabled: boolean;
  host: string;
  port: number;
  encryption: 'tls' | 'ssl' | 'none';
  username: string;
  from_address: string;
  from_name: string;
  password_set: boolean;
}
/** חיבור Fireberry CRM של הדייר — הטוקן לא חוזר, רק token_set. */
export interface FireberryLeadTable { object_type: number; system_name?: string | null; label?: string | null }
/** מיפוי שדות של טבלת הלידים בפיירברי → שדות GoCall. */
export type FireberryFieldMap = Partial<Record<'id' | 'phone' | 'phone2' | 'name' | 'last_name' | 'email' | 'created' | 'modified' | 'status', string | null>>;
export interface FireberryImportSettings {
  enabled: boolean;
  campaign_id: number | null;
  interval_min: number;
  only_new: boolean;
  since?: string | null;
  last_run_at?: string | null;
  last_result?: { ok: boolean; message: string; fetched: number; created: number; updated: number; skipped: number; at: string } | null;
}
export interface FireberrySettings {
  enabled: boolean;
  base_url: string;
  lead_object: 'contact' | 'account';
  token_set: boolean;
  token_hint?: string | null;
  configured: boolean;
  inbound: { url: string; key_prefix: string | null; created_at?: string | null; last_used_at?: string | null };
  presets_installed: number;
  lead_table?: FireberryLeadTable | null;
  field_map?: FireberryFieldMap;
  import?: FireberryImportSettings;
}
export interface FireberryInput {
  enabled?: boolean;
  base_url?: string;
  lead_object?: 'contact' | 'account';
  token?: string;
  lead_table?: FireberryLeadTable | null;
  field_map?: FireberryFieldMap;
  import?: Partial<FireberryImportSettings>;
}
export interface FireberryObject { object_type: number; system_name: string; label: string }
export interface FireberryField { name: string; label: string; type: string }
export interface FireberryImportResult {
  ok: boolean; message: string; fetched: number; created: number; updated: number; skipped: number;
  preview?: Array<{ external_id: string | null; phone: string; name: string | null; email: string | null; status?: unknown }>;
}
export interface FireberryInboundKey {
  id: number;
  key: string;
  prefix: string;
  url: string;
  example_body: Record<string, unknown>;
  headers: Record<string, string>;
}
export interface SmtpInput {
  enabled?: boolean;
  host: string;
  port: number;
  encryption?: 'tls' | 'ssl' | 'none';
  username: string;
  password?: string;
  from_address: string;
  from_name?: string;
}
/** The webhook picker's options — send_webhook targets one of these (spec 12.4). */
export interface HubCatalogWebhook {
  id: number;
  name: string;
  url: string;
  active: boolean;
}

export interface HubCatalog {
  events: Record<string, string>;
  actions: Record<string, string>;
  operators: string[];
  webhooks?: HubCatalogWebhook[];
  capabilities?: HubCapabilities;
}

export type HubTriggerType = 'event' | 'inbound' | 'schedule';
export interface HubCondition { field: string; op: string; value: string }
/**
 * Conditions are either a flat list of rows (legacy, AND-ed) or a grouped
 * object: groups joined by `match` (all=AND, any=OR), rows within a group AND-ed.
 * The builder always writes the grouped shape; the engine accepts both.
 */
export interface HubConditionGroups { match: 'all' | 'any'; groups: HubCondition[][] }
export type HubConditions = HubCondition[] | HubConditionGroups;
export interface HubAction { type: string; config: Record<string, unknown> }
export interface HubAutomation {
  id: number;
  name: string;
  active: boolean;
  trigger_type: HubTriggerType;
  trigger_config: Record<string, unknown>;
  conditions: HubConditions;
  actions: HubAction[];
  runs_count: number;
  last_run_at?: string | null;
  last_status?: string | null;
}
export interface HubAutomationInput {
  name: string;
  trigger_type: HubTriggerType;
  trigger_config?: Record<string, unknown>;
  conditions?: HubConditions;
  actions: HubAction[];
  active?: boolean;
}
export interface HubAutomationStep { action: string; ok: boolean; detail?: string; deferred?: boolean }
export interface HubAutomationTestResult {
  conditions_passed: boolean;
  sample_payload: unknown;
  steps: HubAutomationStep[];
}
export interface HubAutomationRun {
  id: number;
  trigger_event?: string | null;
  trigger_snapshot: unknown;
  status: 'success' | 'partial' | 'failed';
  steps: HubAutomationStep[];
  duration_ms?: number | null;
  created_at: string;
  /** Attempt number; automations retry up to 3 times (spec 15.2). */
  attempts?: number;
  /** When the sweeper will try again; null once exhausted or successful. */
  next_retry_at?: string | null;
  /** The server decides — never infer "can retry" from status on the client. */
  can_retry?: boolean;
}

export interface HubEvent {
  id: number;
  event: string;
  payload: unknown;
  created_at: string;
}

/** הרשאה של מפתח API עם שם ותיאור בעברית (מהשרת). */
export interface ApiScopeMeta { key: string; label: string; description: string; group: string }
export interface HubApiKey {
  id: number;
  name: string;
  prefix: string;
  scopes: string[];
  last_used_at?: string | null;
  expires_at?: string | null;
  created_at: string;
}
export interface ApiInspectorStatus {
  capturing: boolean;
  capture_until: string | null;
  window_minutes: number;
}
export interface ApiInspectorRequest {
  id: number;
  method: string;
  path: string;
  status: number;
  ip?: string | null;
  duration_ms?: number | null;
  error?: string | null;
  captured: boolean;
  headers?: Record<string, string> | null;
  query?: Record<string, unknown> | null;
  body?: Record<string, unknown> | null;
  response_body?: Record<string, unknown> | null;
  created_at?: string | null;
}
export interface ApiTesterResult {
  status: number;
  duration_ms: number;
  url: string;
  body: unknown;
  headers: Record<string, string | null>;
}

/** הגדרת דוח ביצועים (ReportRunner::definitions). */
export interface ReportDef { id: string; he: string; en?: string; chart?: string; description?: string }
